import { h } from "@stencil/core";
const REGEX_TO_REPLACE_PERCENTAGE = /%/g;
const REGEX_TO_TEST_DIP = /^\d+(dip)?$/;
const REGEX_TO_TEST_PERCENTAGE = /^\d+(%)?$/;
export class IntersectionObserverControl {
    constructor() {
        this.bottomMargin = undefined;
        this.leftMargin = undefined;
        this.rightMargin = undefined;
        this.root = undefined;
        this.threshold = undefined;
        this.topMargin = undefined;
    }
    defaultThreshold = [0];
    observer;
    rootElement;
    rootMarginString = "";
    element;
    /**
     * Emitted whenever the control reaches a threshold specified by the threshold property
     * @param IntersectionObserverEntry Details of intersection object.
     */
    intersectionUpdate;
    /**
     * Used to check that the passed value is a valid device-independent-pixel (dip)
     * value, and transform the dip to `px` value.
     * @example
     * Input: "200dip" Output: "200px"
     * Input: "50%" Output: "0px"
     * @param percentValue Represent the given dip value
     * @returns When is a valid dip value returns the value converted to pixels. Otherwise returns "0px".
     */
    checkValidDipValue(dipValue) {
        return REGEX_TO_TEST_DIP.test(dipValue)
            ? this.convertDipToPxValue(dipValue)
            : "0px";
    }
    /**
     * Used to check that the passed value is a valid percentage. @example: "200%"
     * @param percentValue : Represent the given percentage value
     * @returns When is a valid percentage value returns the value, otherwise returns "0px"
     */
    checkValidPercentValue(percentValue) {
        return REGEX_TO_TEST_PERCENTAGE.test(percentValue) ? percentValue : "0px";
    }
    /**
     * @todo TODO: Simplify this implementation
     * Transform the given dip value to pixels
     * @param dipValue Represent the given dip value @example "10dip"
     * @returns Returns the given value converted to px @example "10px"
     */
    convertDipToPxValue(dipValue) {
        const aux = dipValue.replace("dip", "px");
        return aux.split(" ").join("");
    }
    /**
     * Convert the given percentages string representing a threshold value in decimal value
     * @param threshold Threshold string representation @example: "10%"
     * @returns Decimal value between 0 and 1 @example: 0.1
     */
    convertThresholdValueToNumber(threshold) {
        if (REGEX_TO_TEST_PERCENTAGE.test(threshold)) {
            return Number(threshold.replace(REGEX_TO_REPLACE_PERCENTAGE, "")) / 100;
        }
        return 0;
    }
    /**
     * Create a valid Javascript IntersectionObserver API threshold  options value
     * @param threshold Threshold string representation @example: "10%,30%,40%"
     * @returns Valid array of numbers in the range between 0 and 1 @example: [0.1, 0.3, 0.4]
     */
    parseThreshold(threshold) {
        if (!threshold) {
            return [0];
        }
        const finalThresholds = [];
        const thresholdsToParse = threshold.split(",");
        thresholdsToParse.forEach(thresholdValue => {
            const convertedNumber = this.convertThresholdValueToNumber(thresholdValue);
            if (convertedNumber <= 1) {
                finalThresholds.push(convertedNumber);
            }
        });
        return finalThresholds;
    }
    /**
     * Initialize intersection observer with its options and start observing
     */
    setIntersectionObserver() {
        const options = {
            root: this.rootElement,
            rootMargin: this.rootMarginString,
            threshold: this.defaultThreshold
        };
        this.observer = new IntersectionObserver(entries => {
            this.intersectionUpdate.emit(entries[0]);
        }, options);
        const childElement = this.getChildElement();
        if (childElement) {
            this.observer.observe(childElement);
        }
    }
    /**
     * @returns The first child element that its display CSS property is different from `contents`
     */
    getChildElement() {
        let childElement = this.element.firstElementChild;
        while (childElement &&
            getComputedStyle(childElement).display === "contents") {
            childElement = childElement.firstElementChild;
        }
        return childElement;
    }
    /**
     * Transform the given initial intersection-observer component properties
     * into Javascript IntersectionObserver API options valid values.
     */
    setIntersectionObserverOptionsFromProperties() {
        if (this.root) {
            this.rootElement = document.getElementById(this.root);
        }
        this.rootMarginString = [
            this.validatePosition(this.topMargin),
            this.validatePosition(this.leftMargin),
            this.validatePosition(this.bottomMargin),
            this.validatePosition(this.rightMargin)
        ].join(" ");
        this.defaultThreshold = this.parseThreshold(this.threshold);
    }
    /**
     * Check that the given string value is a valid percentage or device
     * independent pixel (dip) value.
     * @param position Represent the given dip or px value
     */
    validatePosition(position) {
        if (position && position.endsWith("dip")) {
            return this.checkValidDipValue(position);
        }
        return this.checkValidPercentValue(position);
    }
    componentDidLoad() {
        this.setIntersectionObserverOptionsFromProperties();
        this.setIntersectionObserver();
    }
    disconnectedCallback() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = undefined;
        }
    }
    render() {
        return h("slot", { name: "content" });
    }
    static get is() { return "ch-intersection-observer"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["intersection-observer.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["intersection-observer.css"]
        };
    }
    static get properties() {
        return {
            "bottomMargin": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Bottom margin around the root element"
                },
                "attribute": "bottom-margin",
                "reflect": false
            },
            "leftMargin": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Left margin around the root element"
                },
                "attribute": "left-margin",
                "reflect": false
            },
            "rightMargin": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Right margin around the root element"
                },
                "attribute": "right-margin",
                "reflect": false
            },
            "root": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set the ID of the component that is used as the viewport, default is the browser."
                },
                "attribute": "root",
                "reflect": false
            },
            "threshold": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Numeric values representing percentages of the target element which are visible."
                },
                "attribute": "threshold",
                "reflect": false
            },
            "topMargin": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Top margin around the root element"
                },
                "attribute": "top-margin",
                "reflect": false
            }
        };
    }
    static get events() {
        return [{
                "method": "intersectionUpdate",
                "name": "intersectionUpdate",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [{
                            "name": "param",
                            "text": "IntersectionObserverEntry Details of intersection object."
                        }],
                    "text": "Emitted whenever the control reaches a threshold specified by the threshold property"
                },
                "complexType": {
                    "original": "IntersectionObserverEntry",
                    "resolved": "IntersectionObserverEntry",
                    "references": {
                        "IntersectionObserverEntry": {
                            "location": "global",
                            "id": "global::IntersectionObserverEntry"
                        }
                    }
                }
            }];
    }
    static get elementRef() { return "element"; }
}
//# sourceMappingURL=intersection-observer.js.map
